import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  formatLatexPreview,
} from "../../../../components/inputs/FractionInput";

// pomocnicze
function gcd(a, b) {
  while (b !== 0) [a, b] = [b, a % b];
  return Math.abs(a);
}

function simplifyFraction(numerator, denominator) {
  const g = gcd(numerator, denominator);
  numerator /= g;
  denominator /= g;
  if (denominator < 0) {
    numerator = -numerator;
    denominator = -denominator;
  }
  return [numerator, denominator];
}

function formatMixedFractionLatex(numerator, denominator) {
  const [n, d] = simplifyFraction(numerator, denominator);
  const sign = n < 0 ? "-" : "";
  const absNum = Math.abs(n);
  const intPart = Math.floor(absNum / d);
  const fracNum = absNum % d;

  if (fracNum === 0) return `${sign}${intPart}`;
  if (intPart === 0) return `${sign}\\dfrac{${fracNum}}{${d}}`;
  return `${sign}${intPart}\\dfrac{${fracNum}}{${d}}`;
}

function formatTerm(coef, variable = "n", power = 1, isFirst = false) {
  if (coef === 0) return "";

  const absCoef = Math.abs(coef);
  const sign = coef < 0 ? "-" : (isFirst ? "" : "+");

  const coefStr = absCoef === 1 && variable !== "" ? "" : absCoef;
  const variableStr = variable + (power === 1 ? "" : `^{${power}}`);

  if (power === 0 || variable === "") {
    return `${sign} ${absCoef}`;
  }

  return `${sign} ${coefStr}${variableStr}`;
}




function getRandomInt(min, max, excludeZero = false) {
  let num;
  do {
    num = Math.floor(Math.random() * (max - min + 1)) + min;
  } while (excludeZero && num === 0);
  return num;
}

export const formulas = [
  // Liniowy: a * n + b
  () => {
    const a = getRandomInt(-5, 5, true);
    const b = getRandomInt(-10, 10);
    const parts = [];
    if (a !== 0) parts.push(formatTerm(a, "n", 1, true));
    if (b !== 0) parts.push(`${b > 0 ? "+ " : "- "}${Math.abs(b)}`);
    return {
      latex: parts.join(" "),
      compute: (n) => [a * n + b, 1],
      params: { a, b }
    };
  },

  // Kwadratowy: a * n^2 + b * n + c
  () => {
    const a = getRandomInt(-3, 3, true);
    const b = getRandomInt(-5, 5);
    const c = getRandomInt(-10, 10);
    const parts = [];
    if (a !== 0) parts.push(formatTerm(a, "n", 2, true));
    if (b !== 0) parts.push(formatTerm(b, "n", 1));
    if (c !== 0) parts.push(`${c > 0 ? "+ " : "- "}${Math.abs(c)}`);
    return {
      latex: parts.join(" "),
      compute: (n) => [a * n * n + b * n + c, 1],
      params: { a, b, c }
    };
  },

  // Wykładniczy: a^n + b
() => {
  const a = getRandomInt(2, 5);
  const b = getRandomInt(-10, 10);
  return {
    latex: `${a}^n ${b < 0 ? '- ' + Math.abs(b) : '+ ' + b}`,
    compute: (n) => [Math.pow(a, n) + b, 1],
    params: { a, b }
  };
},

  // Wymierny: (a * n + b) / (c * n + d)
  () => {
    const numA = getRandomInt(-5, 5, true);
    const numB = getRandomInt(-10, 10);
    const denA = getRandomInt(-3, 3, true);
    const denB = getRandomInt(-10, 10);
    return {
      latex: `\\dfrac{${numA === 1 ? '' : numA === -1 ? '-' : numA}n ${numB >= 0 ? '+ ' + numB : '- ' + Math.abs(numB)}}{${denA === 1 ? '' : denA === -1 ? '-' : denA}n ${denB >= 0 ? '+ ' + denB : '- ' + Math.abs(denB)}}`,
      compute: (n) => [numA * n + numB, denA * n + denB],
      params: { a: numA, b: numB, c: denA, d: denB }
    };
  }
];


export function generateTask(options = {}) {
  let formulaFn, typeSuffix;

  switch (options.forceType) {
    case "linear":
      formulaFn = formulas[0];
      typeSuffix = "Linear";
      break;
    case "quadratic":
      formulaFn = formulas[1];
      typeSuffix = "Quadratic";
      break;
    case "exponential":
      formulaFn = formulas[2];
      typeSuffix = "Exponential";
      break;
    case "rational":
      formulaFn = formulas[3];
      typeSuffix = "Rational";
      break;
    default:
      const index = Math.floor(Math.random() * formulas.length);
      formulaFn = formulas[index];
      typeSuffix = ["Linear", "Quadratic", "Exponential", "Rational"][index];
  }

  const formula = formulaFn(); // zawiera { latex, compute(n), params: {a, b, ...} }
  const n = getRandomInt(2, 6);
  const [numerator, denominator] = formula.compute(n);
  const [numSimple, denSimple] = simplifyFraction(numerator, denominator);

  return {
    type: `FindNthTermFrom${typeSuffix}Task`,
    data: {
      formulaLatex: formula.latex,
      formulaType: options.forceType || typeSuffix.toLowerCase(),
      n,
      ...formula.params, // a, b, c, d itd.
    },
    answer: {
      numerator: numSimple,
      denominator: denSimple,
      raw: `${numSimple}/${denSimple}`,
      parsed: numSimple / denSimple,
    },
  };
}


export function renderTask({ data }) {
  const { formulaLatex, n } = data;
  return (
    <div className="space-y-2">
      <p>Dany jest ciąg określony wzorem:</p>
      <BlockMath math={`a_n = ${formulaLatex}`} />
      <p>Oblicz wartość wyrazu <InlineMath math={`a_${n}`} />.</p>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  const { n } = data;
  return (
    <div className="flex items-center gap-4">
      <span><InlineMath math={`a_${n} = `} /></span>
      <FractionInput
        value={userAnswer || ""}
        onChange={onChange}
        disabled={disabled}
        label={`a_${n}`}
      />
    </div>
  );
}

export function renderAnswer({ answer, data }) {
  const { n } = data;
  const latex = formatMixedFractionLatex(answer.numerator, answer.denominator);
  return <InlineMath math={`a_${n} = ${latex}`} />;
}

export function checkAnswer({ answer, data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;
  const parsed = userAnswer?.parsed;
  if (parsed == null || isNaN(parsed)) return false;

  userAnswer.n = data.n;
  return Math.abs(parsed - answer.parsed) < 0.01; // ← zwiększona tolerancja
}

export function renderExplanation({ data, answer }) {
  return renderExplanationByType({ ...data, answer });
}


function renderExplanationByType({ formulaType, answer, ...data }) {
  if (formulaType === "linear") return renderExplanationLinear({ data });
  if (formulaType === "quadratic") return renderExplanationQuadratic({ data });
  if (formulaType === "exponential") return renderExplanationExponential({ data, answer });

  if (formulaType === "rational") return renderExplanationRational({ data, answer });

  return <i>Brak wyjaśnienia</i>;
}




export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }

  const n = userAnswer.n ?? '?';
  const cleanRaw = userAnswer.raw.replace(/^a\s*=\s*/, ""); // usuń ewentualne "a ="
 const value = formatLatexPreview(cleanRaw, ""); // nie dodaje nic przed =
const latex = `a_{${n}} = ${value}`;


  return (
    <div style={{ marginTop: "1rem" }}>
      <InlineMath math={latex} />
    </div>
  );
}

export function renderExplanationLinear({ data }) {
  const { a, b, n } = data;

  const anLatex = `a_n = ${a === 1 ? '' : a === -1 ? '-' : a}\\cdot n${b === 0 ? '' : b < 0 ? ` - ${-b}` : ` + ${b}`}`;
  const substitutionLatex = `a_{${n}} = ${a === 1 ? '' : a === -1 ? '-' : a}\\cdot ${n}${b === 0 ? '' : b < 0 ? ` - ${-b}` : ` + ${b}`}`;

  const ax = a * n;
  const result = ax + b;

  const calcLatex =
    b === 0
      ? `a_{${n}} = ${ax}`
      : `a_{${n}} = ${ax} ${b < 0 ? `- ${-b}` : `+ ${b}`} = ${result}`;

  return (
    <div className="space-y-4" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1:</strong> Podstawiamy i obliczamy:
        <BlockMath math={anLatex} />
        <BlockMath math={substitutionLatex} />
        {a !== 0 && <BlockMath math={calcLatex} />}
      </div>
    </div>
  );
}


export function renderExplanationQuadratic({ data }) {
  const { a, b, c, n } = data;

  // Krok 1: wzór ogólny i podstawienie
  const formulaLatex = `a_n = ${a !== 1 ? a : ''}n^2 ${b < 0 ? `- ${-b}` : `+ ${b}`}n ${c < 0 ? `- ${-c}` : c > 0 ? `+ ${c}` : ''}`;
  const substitutionLatex = `a_{${n}} = ${a} \\cdot ${n}^2 ${b < 0 ? `- ${Math.abs(b)} \\cdot ${n}` : `+ ${b} \\cdot ${n}`} ${c < 0 ? `- ${Math.abs(c)}` : c > 0 ? `+ ${c}` : ''}`;

  // Krok 2: obliczenia
  const nSquared = n * n;
  const part1 = a * nSquared;
  const part2 = b * n;
  const result = part1 + part2 + c;

  const calcLatex = `a_{${n}} = ${part1} ${part2 < 0 ? `- ${-part2}` : `+ ${part2}`} ${c < 0 ? `- ${-c}` : c > 0 ? `+ ${c}` : ''} = ${result}`;

  return (
    <div className="space-y-4" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1:</strong> Podstawiamy i obliczamy:
        <BlockMath math={formulaLatex} />
        <BlockMath math={substitutionLatex} />
        <BlockMath math={calcLatex} />
      </div>
    </div>
  );
}

export function renderExplanationExponential({ data, answer }) {
  const { a, b, n } = data;
  const { numerator, denominator } = answer;

  if ([a, b, n, numerator, denominator].some(v => v === undefined)) {
    return <i>Brak danych do wyjaśnienia</i>;
  }

  const power = Math.pow(a, n);
  const sum = power + b;
  const finalLatex = formatMixedFractionLatex(numerator, denominator);

  return (
    <div className="space-y-4" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1:</strong> Podstawiamy i obliczamy:
        <BlockMath math={`a_n = ${a}^{n} ${b < 0 ? `- ${-b}` : `+ ${b}`}`} />
        <BlockMath math={`a_{${n}} = ${a}^{${n}} ${b < 0 ? `- ${-b}` : `+ ${b}`}`} />
        <BlockMath math={`a_{${n}}= ${power} ${b < 0 ? `- ${-b}` : `+ ${b}`}  = ${finalLatex}`} />
      </div>
    </div>
  );
}

export function renderExplanationRational({ data, answer }) {
  const { n, a, b, c, d } = data;
  const { numerator, denominator } = answer;

  if ([a, b, c, d, n, numerator, denominator].some(val => val === undefined)) {
    return <i>Brak danych do wyjaśnienia</i>;
  }

  const numMul = a * n;
  const denMul = c * n;
  const numValue = numMul + b;
  const denValue = denMul + d;

  if (denValue === 0) {
    return <i>Nie można wykonać dzielenia przez zero</i>;
  }

  const numSign = b >= 0 ? `+ ${b}` : `- ${Math.abs(b)}`;
  const denSign = d >= 0 ? `+ ${d}` : `- ${Math.abs(d)}`;

  const unsimplifiedLatex = `\\dfrac{${numValue}}{${denValue}}`;
  const simplifiedLatex = `\\dfrac{${numerator}}{${denominator}}`;
  const finalLatex = formatMixedFractionLatex(numerator, denominator);


  return (
    <div className="space-y-4" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1:</strong> Podstawiamy i obliczamy:
        <BlockMath math={`a_n = \\dfrac{${a}n ${numSign}}{${c}n ${denSign}}`} />
        <BlockMath math={`a_{${n}} = \\dfrac{${a} \\cdot ${n} ${numSign}}{${c} \\cdot ${n} ${denSign}}`} />
        <BlockMath math={`a_{${n}} = \\dfrac{${numMul} ${b >= 0 ? "+ " + b : "- " + Math.abs(b)}}{${denMul} ${d >= 0 ? "+ " + d : "- " + Math.abs(d)}}`} />
        <BlockMath math={`a_{${n}} = ${simplifiedLatex}= ${finalLatex}`} />
      </div>
    </div>
  );
}



